import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChartNoAxesCombined, ChevronDown } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '@/hooks/useI18n';
import { useChartTooltipTrigger } from '@/hooks/useChartTooltipTrigger';
import { Skeleton } from '@/components/ui/skeleton';
import { formatInt, formatNumber } from '@/lib/format';
import { formatAnalyticsBucket } from '@/lib/analyticsSeries';
import { cn } from '@/lib/cn';
import type { OverviewSeriesResponse } from '@/types/api';

interface OverviewChartProps {
  data: OverviewSeriesResponse | undefined;
  loading: boolean;
}

export function OverviewChart({ data, loading }: OverviewChartProps) {
  const { t, locale } = useI18n();
  const tooltipTrigger = useChartTooltipTrigger();
  const [metric, setMetric] = useState<'viewsDelta' | 'followers'>('viewsDelta');
  const gradientId = useId();
  const points = (data?.[metric] ?? []).map((point) => ({
    ...point,
    bucket: point.date ?? point.timestamp,
  }));
  const label =
    metric === 'viewsDelta' ? t('Vues gagnées', 'Views gained') : t('Abonnés', 'Followers');
  const color = metric === 'viewsDelta' ? 'hsl(var(--violet))' : 'hsl(var(--emerald))';
  const latest = points.at(-1);

  return (
    <section
      aria-label={t('Évolution des performances', 'Performance trend')}
      aria-busy={loading}
      className="flex min-w-0 flex-col rounded-2xl border border-line-1 bg-bg-1 shadow-card"
    >
      <header className="flex flex-col gap-4 px-5 pb-2 pt-5 sm:flex-row sm:items-start sm:justify-between sm:px-6 sm:pt-6">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight text-text-1">
            {t('Évolution des performances', 'Performance trend')}
          </h2>
          <p className="mt-1 text-[12px] text-text-3">
            {metric === 'viewsDelta'
              ? t('Vues gagnées chaque jour', 'Views gained each day')
              : t('Évolution du nombre d’abonnés', 'Follower count over time')}
          </p>
        </div>
        <div
          className="flex w-fit shrink-0 items-center gap-1 rounded-lg bg-bg-2 p-1"
          aria-label={t('Mesure affichée', 'Displayed metric')}
        >
          {(['viewsDelta', 'followers'] as const).map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={metric === key}
              onClick={() => setMetric(key)}
              className={cn(
                'min-h-9 rounded-md px-3 text-[11px] font-medium transition-colors sm:min-h-8',
                metric === key ? 'bg-bg-1 text-text-1 shadow-sm' : 'text-text-3 hover:text-text-1',
              )}
            >
              {key === 'viewsDelta' ? t('Vues gagnées', 'Views gained') : t('Abonnés', 'Followers')}
            </button>
          ))}
        </div>
      </header>

      <div className="flex min-h-[265px] min-w-0 flex-1 flex-col justify-center px-3 pb-3 pt-5 sm:min-h-[290px] sm:px-5">
        {loading ? (
          <Skeleton className="mx-2 h-56 rounded-xl" />
        ) : !points.length ? (
          <div className="flex min-h-56 flex-col items-center justify-center gap-2 px-4 text-center">
            <span className="mb-1 flex size-10 items-center justify-center rounded-xl bg-violet-soft text-violet-deep">
              <ChartNoAxesCombined className="size-5" aria-hidden="true" />
            </span>
            <p className="text-[13px] font-medium text-text-2">
              {t('Aucune mesure sur cette période', 'No measurements in this period')}
            </p>
            <p className="max-w-64 text-[12px] leading-relaxed text-text-3">
              {t(
                'Les premières données apparaîtront après la collecte de vos comptes.',
                'Your first data will appear after your accounts are collected.',
              )}
            </p>
          </div>
        ) : (
          <div
            role="img"
            aria-label={t(
              `${label} · ${points.length} mesures · dernière mesure : ${formatNumber(latest?.value ?? 0)}`,
              `${label} · ${points.length} measurements · latest: ${formatNumber(latest?.value ?? 0)}`,
            )}
            className="h-[245px] w-full min-w-0"
          >
            <div aria-hidden="true" className="h-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.16} />
                      <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke="hsl(var(--line-1))"
                    strokeDasharray="3 5"
                  />
                  <XAxis
                    dataKey="bucket"
                    tickFormatter={(value: string) => formatAnalyticsBucket(value, 'DAY', locale)}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--text-3))', fontSize: 10 }}
                    minTickGap={32}
                    tickMargin={12}
                  />
                  <YAxis
                    tickFormatter={formatNumber}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--text-3))', fontSize: 10 }}
                    width={44}
                    tickMargin={8}
                  />
                  <Tooltip
                    trigger={tooltipTrigger}
                    labelFormatter={(value: string) =>
                      formatAnalyticsBucket(value, 'DAY', locale, true)
                    }
                    formatter={(value: number) => [formatNumber(value), label]}
                    contentStyle={{
                      background: 'hsl(var(--bg-1))',
                      border: '1px solid hsl(var(--line-1))',
                      borderRadius: 12,
                      fontSize: 12,
                      boxShadow: '0 4px 20px hsl(var(--text-1) / 0.08)',
                    }}
                    cursor={{ stroke: color, strokeDasharray: '3 3', opacity: 0.4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={color}
                    strokeWidth={2.5}
                    fill={`url(#${gradientId})`}
                    isAnimationActive={false}
                    dot={points.length === 1 ? { r: 4, fill: color } : false}
                    activeDot={{ r: 4, strokeWidth: 3, stroke: 'hsl(var(--bg-1))' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-line-1 px-5 py-3.5 text-[11px] sm:px-6">
        <span className="flex items-center gap-2 text-text-3">
          <span className="size-1.5 rounded-full bg-violet" aria-hidden="true" />
          {t('Mesures quotidiennes · 30 jours', 'Daily measurements · 30 days')}
        </span>
        <Link
          to="/analytics"
          className="inline-flex min-h-8 items-center gap-1.5 font-medium text-violet-deep hover:underline"
        >
          {t('Analyse détaillée', 'Detailed analytics')}
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </Link>
      </footer>
      {!loading && points.length > 0 && (
        <details className="group border-t border-line-1 px-5 sm:px-6">
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 text-[11px] font-medium text-text-3 hover:text-violet-deep [&::-webkit-details-marker]:hidden">
            <span>{t('Voir les mesures', 'View measurements')}</span>
            <ChevronDown
              className="size-3.5 transition-transform group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>
          <div
            tabIndex={0}
            role="region"
            aria-label={t('Valeurs du graphique', 'Chart values')}
            className="mb-4 max-h-52 overflow-auto rounded-lg border border-line-1"
          >
            <table
              aria-label={t('Mesures quotidiennes', 'Daily measurements')}
              className="w-full text-left text-[11px]"
            >
              <thead className="sticky top-0 bg-bg-2 text-text-2">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Date
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    {label}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-1">
                {points.map((point) => (
                  <tr key={point.timestamp}>
                    <td className="px-3 py-2 text-text-3">
                      {formatAnalyticsBucket(point.bucket, 'DAY', locale, true)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-text-1">
                      {formatInt(point.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}
